import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  adminCreateDrink,
  adminDeleteDrink,
  adminListDrinks,
  adminSetServiceStatus,
  type DrinkServiceStatus,
} from "../../lib/api";
import type {
  AdminDrinkListItem,
  AdminStatusFilter,
} from "../../types/admin";
import type { SessionUser } from "../../types/auth";
import RecipeStudioTabs from "../../components/admin/RecipeStudioTabs";
import { useMediaQuery, PHONE_TAP_QUERY } from "../../lib/useMediaQuery";

const STATUS_FILTERS: { value: AdminStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "testing", label: "Testing" },
  { value: "needs_review", label: "Needs Review" },
  { value: "approved", label: "Approved" },
  { value: "recently_updated", label: "Recently Updated" },
  { value: "hidden", label: "Hidden" },
  { value: "86d", label: "86'd" },
  { value: "retired", label: "Retired" },
];

export default function AdminRecipes() {
  const navigate = useNavigate();
  const { user } = useOutletContext<{ user: SessionUser }>();
  const isOwner = user.bar_roles.includes("bar_owner");
  const isManager = isOwner || user.bar_roles.includes("bar_manager");
  const tapPhone = useMediaQuery(PHONE_TAP_QUERY);

  const [statusFilter, setStatusFilter] = useState<AdminStatusFilter>("all");
  const [query, setQuery] = useState("");
  const [categoryView, setCategoryView] = useState(""); // "" | "__grouped" | specific category
  const [drinks, setDrinks] = useState<AdminDrinkListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [busyToggleId, setBusyToggleId] = useState<string | null>(null);

  // New-cocktail inline modal (replaces prompt()/alert() which are broken in iOS standalone PWA).
  const [newCocktailOpen, setNewCocktailOpen] = useState(false);
  const [newCocktailName, setNewCocktailName] = useState("");
  const [newCocktailCategory, setNewCocktailCategory] = useState("Whiskey");
  const [newCocktailBusy, setNewCocktailBusy] = useState(false);
  const [newCocktailError, setNewCocktailError] = useState<string | null>(null);

  useEffect(() => {
    // Debounce + cancellation: every keystroke would otherwise fire an
    // adminListDrinks(). A slow earlier response could land after a
    // later one and overwrite the visible results with stale data.
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setLoadingList(true);
      setListError(null);
      adminListDrinks(statusFilter, query.trim())
        .then((r) => { if (!cancelled) setDrinks(r.drinks); })
        .catch((err) => { if (!cancelled) setListError(err?.message ?? "Could not load drinks"); })
        .finally(() => { if (!cancelled) setLoadingList(false); });
    }, query.trim() ? 200 : 0); // immediate on filter changes, debounced for typed queries
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [statusFilter, query]);

  async function onDeleteDrinkConfirm(drink_id: string) {
    setDeletingId(drink_id);
    setListError(null);
    try {
      await adminDeleteDrink(drink_id);
      setDrinks((prev) => prev.filter((x) => x.id !== drink_id));
      setConfirmDeleteId(null);
      setDeletingId(null);
    } catch (err: any) {
      setListError(err?.message ?? "Could not delete recipe");
      setDeletingId(null);
    }
  }

  async function onNewCocktailSubmit() {
    const name = newCocktailName.trim();
    if (!name) return;
    setNewCocktailBusy(true);
    setNewCocktailError(null);
    try {
      const resp = await adminCreateDrink(name, newCocktailCategory.trim() || "Whiskey");
      setNewCocktailOpen(false);
      setNewCocktailName("");
      setNewCocktailCategory("Whiskey");
      navigate(`/admin/recipes/${resp.slug}/edit/${resp.version_id}`);
    } catch (err: any) {
      setNewCocktailError(err?.message ?? "Could not create drink");
    } finally {
      setNewCocktailBusy(false);
    }
  }

  async function onToggleActive(drinkId: string, currentStatus: string) {
    const newStatus: DrinkServiceStatus = currentStatus === "Active" ? "Hidden" : "Active";
    setBusyToggleId(drinkId);
    setListError(null);
    try {
      await adminSetServiceStatus(drinkId, newStatus);
      const r = await adminListDrinks(statusFilter, query.trim()).catch(() => null);
      if (r) setDrinks(r.drinks);
    } catch (err: any) {
      // Surface the error so a silent rollback isn't mysterious; the toggle's
      // disabled state already blocks drinks without an approved version, so
      // anything that gets here is a real failure.
      setListError(err?.message ?? "Could not toggle service status");
    } finally {
      setBusyToggleId(null);
    }
  }

  function renderDrinkRow(d: AdminDrinkListItem) {
    const isConfirming = confirmDeleteId === d.id;
    const isDeleting = deletingId === d.id;
    const isOn = d.service_status === "Active";
    const canToggle = isOn || d.display_status === "Approved";
    const toggleBusy = busyToggleId === d.id;
    // On phones both buttons are 44px wide (global.css tap targets), so the toggle sits clear of
    // the ✕ (right 6–50) instead of overlapping it, and the row's text stops short of both.
    const rightPad = tapPhone ? (isOwner ? 100 : isManager ? 56 : undefined) : isOwner ? 72 : isManager ? 44 : undefined;
    const toggleRight = tapPhone ? (isOwner ? 52 : 8) : isOwner ? 38 : 8;
    return (
      <li key={d.id} style={{ position: "relative" }}>
        {isConfirming ? (
          <div style={styles.deleteConfirmRow}>
            <span style={styles.deleteConfirmText}>Delete "{d.name}"?</span>
            <div style={styles.deleteConfirmBtns}>
              <button
                type="button"
                onClick={() => onDeleteDrinkConfirm(d.id)}
                disabled={isDeleting}
                style={styles.deleteConfirmBtn}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                disabled={isDeleting}
                style={styles.deleteCancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => navigate(`/admin/recipes/${d.slug}`)}
            style={{ ...styles.row, paddingRight: rightPad }}
          >
            <div style={styles.rowName}>{d.name}</div>
            <div style={styles.rowChips}>
              <Chip
                label={d.display_status}
                tone={d.display_status === "Approved" ? "approved" : "muted"}
              />
              <Chip label={d.service_status} tone="muted" />
              <Chip label={`v${d.display_version_number ?? "—"}`} tone="muted" />
            </div>
            <div style={styles.rowMeta}>
              {d.display_approved_at
                ? `Approved ${new Date(d.display_approved_at).toLocaleDateString()}`
                : `Updated ${new Date(d.updated_at).toLocaleDateString()}`}
              {d.updated_by_name && d.updated_by_name !== "—" && ` · ${d.updated_by_name}`}
            </div>
          </button>
        )}
        {isManager && !isConfirming && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (canToggle && !toggleBusy) onToggleActive(d.id, d.service_status);
            }}
            disabled={!canToggle || toggleBusy}
            title={
              toggleBusy ? "Saving…"
              : isOn ? "On menu — click to hide from bartenders"
              : canToggle ? "Off menu — click to make visible to bartenders"
              : "Needs an approved recipe version to go on menu"
            }
            style={{
              ...styles.toggleBtn,
              right: toggleRight,
              opacity: canToggle ? 1 : 0.35,
              cursor: canToggle && !toggleBusy ? "pointer" : "default",
            }}
          >
            <span style={{ ...styles.toggleTrack, background: isOn ? "var(--gold)" : "var(--border)" }}>
              <span style={{ ...styles.toggleThumb, transform: isOn ? "translateX(14px)" : "translateX(0)" }} />
            </span>
          </button>
        )}
        {isOwner && !isConfirming && (
          <button
            type="button"
            onClick={() => setConfirmDeleteId(d.id)}
            style={styles.deleteXBtn}
            title="Delete recipe"
          >
            ×
          </button>
        )}
      </li>
    );
  }

  const isGrouped = categoryView === "__grouped";
  const categoryFilter = !isGrouped && categoryView !== "" ? categoryView : null;
  const visibleDrinks = categoryFilter ? drinks.filter((d) => d.category === categoryFilter) : drinks;
  const categories = [...new Set(drinks.map((d) => d.category))].sort();

  const categoryGroups: [string, AdminDrinkListItem[]][] = isGrouped
    ? Object.entries(
        drinks.reduce<Record<string, AdminDrinkListItem[]>>((acc, d) => {
          (acc[d.category] ??= []).push(d);
          return acc;
        }, {})
      )
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([cat, items]) => [cat, [...items].sort((a, b) => a.name.localeCompare(b.name))])
    : [];

  return (
    <section style={styles.listPanel}>
      <RecipeStudioTabs activeTab="cocktails" badgeCounts={{ cocktails: drinks.length }} />
      <button type="button" onClick={() => { setNewCocktailOpen(true); setNewCocktailError(null); }} style={styles.newCocktailBtn}>
        + New Cocktail
      </button>

      {newCocktailOpen && (
        <div style={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="new-cocktail-title">
          <div style={styles.modalBox}>
            <h2 id="new-cocktail-title" style={styles.modalTitle}>New Cocktail</h2>
            <div style={styles.modalField}>
              <label htmlFor="nc-name" style={styles.modalLabel}>Name</label>
              <input
                id="nc-name"
                type="text"
                value={newCocktailName}
                onChange={(e) => setNewCocktailName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !newCocktailBusy) void onNewCocktailSubmit(); }}
                placeholder="e.g. Smoked Old Fashioned"
                autoFocus
                disabled={newCocktailBusy}
                style={styles.modalInput}
              />
            </div>
            <div style={styles.modalField}>
              <label htmlFor="nc-category" style={styles.modalLabel}>Category</label>
              <input
                id="nc-category"
                type="text"
                value={newCocktailCategory}
                onChange={(e) => setNewCocktailCategory(e.target.value)}
                placeholder="e.g. Whiskey, Clarified, Rum"
                disabled={newCocktailBusy}
                style={styles.modalInput}
              />
            </div>
            {newCocktailError && <p style={styles.modalError}>{newCocktailError}</p>}
            <div style={styles.modalBtns}>
              <button
                type="button"
                onClick={() => { setNewCocktailOpen(false); setNewCocktailName(""); setNewCocktailCategory("Whiskey"); }}
                disabled={newCocktailBusy}
                style={styles.modalCancelBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onNewCocktailSubmit()}
                disabled={newCocktailBusy || !newCocktailName.trim()}
                style={newCocktailBusy || !newCocktailName.trim() ? styles.modalSubmitBtnDisabled : styles.modalSubmitBtn}
              >
                {newCocktailBusy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={styles.listFilters}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          style={styles.searchInput}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as AdminStatusFilter)}
          style={styles.statusSelect}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          value={categoryView}
          onChange={(e) => setCategoryView(e.target.value)}
          style={styles.statusSelect}
        >
          <option value="">All Categories</option>
          <option value="__grouped">Group by Category</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {loadingList && <p style={styles.muted}>Loading…</p>}
      {listError && <p style={styles.error}>{listError}</p>}
      {!loadingList && !listError && visibleDrinks.length === 0 && !isGrouped && (
        <p style={styles.muted}>No recipes match.</p>
      )}

      {isGrouped ? (
        <div style={{ display: "grid", gap: 16 }}>
          {categoryGroups.map(([cat, items]) => (
            <div key={cat}>
              <div style={styles.categoryHeader}>
                <span style={styles.categoryLabel}>{cat}</span>
                <span style={styles.categoryCount}>{items.length}</span>
              </div>
              <ul style={styles.list}>
                {items.map((d) => renderDrinkRow(d))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <ul style={styles.list}>
          {visibleDrinks.map((d) => renderDrinkRow(d))}
        </ul>
      )}
    </section>
  );
}

// ─────────────────────────── Chip ───────────────────────────

function Chip({ label, tone }: { label: string; tone: "gold" | "muted" | "approved" }) {
  return <span style={{ ...styles.chip, ...chipTone[tone] }}>{label}</span>;
}

const chipTone: Record<string, CSSProperties> = {
  gold:     { background: "rgba(249,175,54,0.12)", color: "var(--gold)", borderColor: "rgba(249,175,54,0.3)" },
  muted:    { background: "var(--bg)",             color: "var(--muted)", borderColor: "var(--border)"      },
  approved: { background: "rgba(39,174,96,0.16)",  color: "#86efac",     borderColor: "rgba(39,174,96,0.3)" },
};

// ─────────────────────────── Styles ───────────────────────────

const styles: Record<string, CSSProperties> = {
  listPanel: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 12,
    maxWidth: 480,
  },
  listFilters: {
    display: "flex",
    gap: 6,
    marginBottom: 10,
    width: "100%",
    minWidth: 0,
    flexWrap: "wrap",
  },
  searchInput: {
    flex: "1 1 140px",
    minWidth: 0,
    height: 36,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "0 10px",
    fontSize: 13,
    outline: "none",
  },
  statusSelect: {
    flex: "0 1 auto",
    minWidth: 0,
    maxWidth: "100%",
    height: 36,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    fontSize: 12,
    fontWeight: 700,
    padding: "0 8px",
  },
  list: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 },
  toggleBtn: {
    position: "absolute" as const,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    padding: 2,
    lineHeight: 0,
  } as CSSProperties,
  toggleTrack: {
    display: "inline-flex",
    alignItems: "center",
    width: 30,
    height: 16,
    borderRadius: 8,
    padding: 1,
    transition: "background 0.15s",
    flexShrink: 0,
  } as CSSProperties,
  toggleThumb: {
    width: 13,
    height: 13,
    borderRadius: "50%",
    background: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
    transition: "transform 0.15s",
    flexShrink: 0,
  } as CSSProperties,
  deleteXBtn: {
    position: "absolute",
    top: "50%",
    right: 6,
    transform: "translateY(-50%)",
    background: "transparent",
    color: "var(--muted)",
    border: "none",
    borderRadius: 6,
    width: 26,
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
  },
  deleteConfirmRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "8px 10px",
    background: "rgba(224,80,64,0.06)",
    border: "1px solid rgba(224,80,64,0.3)",
    borderRadius: 10,
  },
  deleteConfirmText: {
    flex: 1,
    fontSize: 12,
    color: "var(--text)",
    fontWeight: 600,
    minWidth: 0,
  },
  deleteConfirmBtns: { display: "flex", gap: 6, alignItems: "center" },
  deleteConfirmBtn: {
    background: "#E05040",
    color: "#fff",
    border: 0,
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 30,
  },
  deleteCancelBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 30,
  },
  row: {
    width: "100%",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    background: "var(--surface)",
    color: "var(--text)",
    cursor: "pointer",
    textAlign: "left",
  },
  rowName: {
    fontFamily: "Georgia, serif",
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 4,
    lineHeight: 1.15,
  },
  rowChips: { display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 },
  rowMeta: { fontSize: 11, color: "var(--muted)", fontWeight: 600 },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 4px 6px",
    borderBottom: "1px solid var(--border)",
    marginBottom: 6,
  },
  categoryLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  categoryCount: {
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "1px 7px",
  },
  newCocktailBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    marginBottom: 10,
    width: "100%",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 7px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  muted: { color: "var(--muted)", fontSize: 13, padding: "8px 0" },
  error: {
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    padding: 10,
    borderRadius: 10,
    fontSize: 13,
  },
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 200, padding: 16,
  },
  modalBox: {
    background: "var(--surface)", border: "1px solid var(--border)",
    borderRadius: 14, padding: 24, width: "100%", maxWidth: 380,
    display: "flex", flexDirection: "column", gap: 14,
  },
  modalTitle: { fontFamily: "Georgia, serif", fontSize: 18, margin: 0, color: "var(--text)" },
  modalField: { display: "flex", flexDirection: "column", gap: 6 },
  modalLabel: { fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--muted)" },
  modalInput: {
    background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)",
    borderRadius: 8, padding: "8px 10px", fontSize: 14, fontFamily: "inherit",
  },
  modalError: { color: "#FF8A7A", fontSize: 12, margin: 0 },
  modalBtns: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 },
  modalCancelBtn: {
    padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)",
    background: "transparent", color: "var(--muted)", fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  modalSubmitBtn: {
    padding: "8px 16px", borderRadius: 8, border: 0,
    background: "var(--gold)", color: "#0D0B08", fontSize: 13, fontWeight: 800, cursor: "pointer",
  },
  modalSubmitBtnDisabled: {
    padding: "8px 16px", borderRadius: 8, border: 0,
    background: "rgba(249,175,54,0.4)", color: "#0D0B08", fontSize: 13, fontWeight: 800, cursor: "not-allowed",
  },
};
